import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  FiFileText, 
  FiUsers, 
  FiActivity, 
  FiUserCheck, 
  FiCalendar, 
  FiBarChart2,
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiArrowLeft,
  FiLoader
} from 'react-icons/fi';
import { getApiUrl } from './config';

// Sidebar component
const Sidebar = ({ activeTab, onTabChange }) => {
  const tabs = [
    { id: 'publications', name: 'Publications', icon: FiFileText },
    { id: 'conferences', name: 'Conferences', icon: FiUsers },
    { id: 'trial-studies', name: 'Clinical Trials', icon: FiActivity },
    { id: 'kol-engagements', name: 'KOL Engagements', icon: FiUserCheck },
    { id: 'activities', name: 'Activities', icon: FiCalendar },
    { id: 'integrated-evidence', name: 'Integrated Evidence', icon: FiBarChart2 }
  ];

  return (
    <div className="w-64 lg:w-80 bg-white shadow-medium border-r border-gray-100 h-screen fixed left-0 top-0 z-10">
      <div className="p-4 lg:p-8">
        <div className="mb-8 lg:mb-12">
          <div className="flex items-center space-x-3 mb-3">
            <img 
              src="/logo.svg" 
              alt="MDash Edge Logo" 
              className="w-8 h-8 lg:w-10 lg:h-10"
            />
            <h1 className="text-2xl lg:text-3xl font-bold" style={{color: '#0B2C61'}}>MDash Edge</h1>
          </div>
          <p className="text-sm lg:text-base mb-3" style={{color: '#0B2C61'}}>Medical Project Management and Tracking application</p>
          <div className="w-12 h-1 bg-gradient-to-r from-medical-500 to-medical-600 rounded-full"></div>
        </div>
        
        <nav className="space-y-2 lg:space-y-3">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`w-full flex items-center space-x-3 lg:space-x-4 px-4 lg:px-6 py-3 lg:py-4 rounded-xl transition-all duration-200 group ${
                  isActive
                    ? 'bg-gradient-to-r from-medical-500 to-medical-600 text-white shadow-soft'
                    : 'text-secondary-600 hover:bg-medical-50 hover:text-medical-600'
                }`}
              >
                <IconComponent 
                  className={`text-lg lg:text-xl transition-colors duration-200 flex-shrink-0 ${
                    isActive ? 'text-white' : 'text-secondary-500 group-hover:text-medical-500'
                  }`} 
                />
                <span className={`font-medium text-xs lg:text-sm transition-colors duration-200 truncate ${
                  isActive ? 'text-white' : 'text-secondary-700 group-hover:text-medical-600'
                }`}>
                  {tab.name}
                </span>
                {isActive && (
                  <div className="ml-auto w-2 h-2 bg-white rounded-full flex-shrink-0"></div>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

// Search and filter component
const SearchAndFilter = ({ onSearch, onFilter, type, currentSearchTerm, currentFilters, filterOptions }) => {
  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchTerm = formData.get('search') || '';
    onSearch(searchTerm);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...currentFilters, [key]: value };
    onFilter(newFilters);
  };

  const getFilterOptions = () => {
    if (!filterOptions) return [];
    
    switch (type) {
      case 'publications':
        return [
          { key: 'therapeutic_area', label: 'Therapeutic Area', options: filterOptions.therapeutic_area || [] },
          { key: 'journal', label: 'Journal', options: filterOptions.journal || [] },
          { key: 'drug', label: 'Drug', options: filterOptions.drug || [] },
          { key: 'region', label: 'Region', options: filterOptions.region || [] },
          { key: 'access_type', label: 'Access Type', options: filterOptions.access_type || [] },
          { key: 'status', label: 'Status', options: filterOptions.status || [] }
        ];
      case 'conferences':
        return [
          { key: 'presentation_type', label: 'Presentation Type', options: filterOptions.presentation_type || [] },
          { key: 'therapeutic_area', label: 'Therapeutic Area', options: filterOptions.therapeutic_area || [] },
          { key: 'drug', label: 'Drug', options: filterOptions.drug || [] },
          { key: 'location', label: 'Location', options: filterOptions.location || [] },
          { key: 'organizer', label: 'Organizer', options: filterOptions.organizer || [] }
        ];
      case 'trial-studies':
        return [
          { key: 'phase', label: 'Phase', options: filterOptions.phase || [] },
          { key: 'indication', label: 'Indication', options: filterOptions.indication || [] },
          { key: 'status', label: 'Status', options: filterOptions.status || [] },
          { key: 'therapeutic_area', label: 'Therapeutic Area', options: filterOptions.therapeutic_area || [] },
          { key: 'drug', label: 'Drug', options: filterOptions.drug || [] }
        ];
      case 'kol-engagements':
        return [
          { key: 'engagement_type', label: 'Engagement Type', options: filterOptions.engagement_type || [] },
          { key: 'therapeutic_area', label: 'Therapeutic Area', options: filterOptions.therapeutic_area || [] },
          { key: 'region', label: 'Region', options: filterOptions.region || [] },
          { key: 'strategic_importance', label: 'Strategic Importance', options: filterOptions.strategic_importance || [] }
        ];
      case 'activities':
        return [
          { key: 'type', label: 'Type', options: filterOptions.type || [] },
          { key: 'status', label: 'Status', options: filterOptions.status || [] },
          { key: 'therapeutic_area', label: 'Therapeutic Area', options: filterOptions.therapeutic_area || [] },
          { key: 'drug', label: 'Drug', options: filterOptions.drug || [] },
          { key: 'region', label: 'Region', options: filterOptions.region || [] }
        ];
      case 'integrated-evidence':
        return [
          { key: 'evidence_type', label: 'Evidence Type', options: filterOptions.evidence_type || [] },
          { key: 'therapeutic_area', label: 'Therapeutic Area', options: filterOptions.therapeutic_area || [] },
          { key: 'data_source', label: 'Data Source', options: filterOptions.data_source || [] }
        ];
      default:
        return [];
    }
  };

  return (
    <div className="bg-white rounded-xl lg:rounded-2xl shadow-soft border border-gray-100 p-4 lg:p-6 mb-4 lg:mb-6">
      <form onSubmit={handleSearch} className="mb-4 lg:mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-4 w-4 lg:h-5 lg:w-5 text-secondary-400" />
          </div>
          <input
            type="text"
            name="search"
            placeholder="Search records..."
            defaultValue={currentSearchTerm}
            className="w-full pl-10 lg:pl-12 pr-20 lg:pr-24 py-2 lg:py-3 border border-gray-200 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-medical-500 focus:border-transparent bg-gray-50 text-secondary-700 placeholder-secondary-400 transition-all duration-200 text-sm lg:text-base"
          />
          <button
            type="submit"
            className="absolute right-1 top-1 bottom-1 px-3 lg:px-4 bg-gradient-to-r from-medical-500 to-medical-600 text-white rounded-md lg:rounded-lg hover:from-medical-600 hover:to-medical-700 transition-all duration-200 font-medium text-xs lg:text-sm shadow-soft"
          >
            Search
          </button>
        </div>
      </form>

      <div className="flex items-center space-x-2 mb-3 lg:mb-4">
        <FiFilter className="h-4 w-4 lg:h-5 lg:w-5 text-secondary-500" />
        <h3 className="text-sm lg:text-base font-semibold text-secondary-700">Filters</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        {getFilterOptions().map((filter) => (
          <div key={filter.key} className="space-y-1 lg:space-y-2">
            <label className="block text-xs lg:text-sm font-medium text-secondary-600">
              {filter.label}
            </label>
            <select
              value={currentFilters[filter.key] || ''}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
              className="w-full px-3 lg:px-4 py-2 lg:py-2.5 border border-gray-200 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-medical-500 focus:border-transparent bg-gray-50 text-secondary-700 transition-all duration-200 text-xs lg:text-sm"
            >
              <option value="">All {filter.label}</option>
              {filter.options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

// Pagination component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="flex justify-center items-center space-x-2 mt-4 lg:mt-6">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 border border-gray-200 rounded-lg lg:rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-secondary-600 transition-all duration-200 text-xs lg:text-sm"
      >
        <FiChevronLeft className="h-3 w-3 lg:h-4 lg:w-4" />
        <span className="hidden sm:inline">Previous</span>
      </button>
      
      {getPageNumbers().map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`px-3 lg:px-4 py-2 rounded-lg lg:rounded-xl transition-all duration-200 text-xs lg:text-sm ${
            currentPage === page
              ? 'bg-gradient-to-r from-medical-500 to-medical-600 text-white shadow-soft'
              : 'border border-gray-200 hover:bg-gray-50 text-secondary-600'
          }`}
        >
          {page}
        </button>
      ))}
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 border border-gray-200 rounded-lg lg:rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-secondary-600 transition-all duration-200 text-xs lg:text-sm"
      >
        <span className="hidden sm:inline">Next</span>
        <FiChevronRight className="h-3 w-3 lg:h-4 lg:w-4" />
      </button>
    </div>
  );
};

// Data table component
const DataTable = ({ data, columns, onRowClick, type, loading, searchLoading }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <FiLoader className="h-8 w-8 text-medical-500 animate-spin" />
          <p className="text-secondary-500">Loading data...</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiFileText className="h-8 w-8 text-gray-400" />
        </div>
        <div className="text-secondary-400 text-lg font-medium">No data available</div>
        <div className="text-secondary-300 text-sm mt-2">Try adjusting your search or filters</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl lg:rounded-2xl shadow-soft border border-gray-100 overflow-hidden flex flex-col h-full">
      {searchLoading && (
        <div className="bg-gradient-to-r from-medical-50 to-medical-100 border-b border-medical-200 px-4 lg:px-6 py-2 lg:py-3">
          <div className="flex items-center space-x-2 lg:space-x-3">
            <FiLoader className="h-4 w-4 lg:h-5 lg:w-5 text-medical-500 animate-spin" />
            <span className="text-xs lg:text-sm font-medium text-medical-600">Searching...</span>
          </div>
        </div>
      )}
      
      <div className="overflow-x-auto flex-1">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className="px-3 lg:px-6 py-2 lg:py-3 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider whitespace-nowrap"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {data.map((item, index) => (
              <tr
                key={item.id || index}
                className="hover:bg-gradient-to-r hover:from-medical-50 hover:to-medical-100 cursor-pointer transition-all duration-200 group"
                onClick={() => onRowClick(item)}
              >
                {Object.values(item).slice(1).map((value, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="px-3 lg:px-6 py-2 lg:py-3 whitespace-nowrap text-xs lg:text-sm text-secondary-700 group-hover:text-secondary-800 transition-colors duration-200"
                  >
                    {value || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Detail page component
const DetailPage = ({ item, type, onBack }) => {
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetailData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(getApiUrl(`${type}/${item.id}`));
        setDetailData(response.data);
      } catch (error) {
        console.error('Error fetching detail data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetailData();
  }, [item.id, type]);

  const getMainColumns = () => {
    switch (type) {
      case 'publications':
        return ['Publication ID', 'Title', 'Journal', 'Drug', 'Authors', 'Publication Date', 'Status'];
      case 'conferences':
        return ['Conference ID', 'Name', 'Abstract Title', 'Drug', 'Conference Date', 'Location', 'Presentation Type'];
      case 'trial-studies':
        return ['Trial ID', 'NCT Number', 'Title', 'Drug', 'Indication', 'Phase', 'Status', 'Trial Sponsor', 'Estimated Completion Date'];
      case 'kol-engagements':
        return ['KOL ID', 'KOL Name', 'Affiliation', 'Engagement Type', 'Date', 'Topic', 'Strategic Importance'];
      case 'activities':
        return ['Activity ID', 'Title', 'Type', 'Drug', 'Owner', 'Status', 'End Date'];
      case 'integrated-evidence':
        return ['Evidence ID', 'Title', 'Evidence Type', 'Therapeutic Area', 'Lead', 'Completion Date'];
      default:
        return [];
    }
  };

  const getDetailColumns = () => {
    switch (type) {
      case 'publications':
        return ['Corresponding Author', 'DOI', 'PMID', 'Abstract', 'Therapeutic Area', 'Impact Factor', 'Citations', 'Region', 'Access Type', 'Publisher', 'Submission Date', 'Keywords', 'Linked Trial ID', 'Linked Conference ID'];
      case 'conferences':
        return ['End Date', 'Organizer', 'Therapeutic Area', 'Presenter', 'Indication', 'Outcome Highlights', 'Attendees', 'Booth Number', 'Follow Up Actions', 'Role', 'Linked Trial ID', 'Linked Publication ID'];
      case 'trial-studies':
        return ['Therapeutic Area', 'Trial Design', 'Primary Endpoint', 'Secondary Endpoints', 'Start Date', 'Principal Investigator', 'Sites', 'Patients Enrolled', 'Results Summary', 'Linked Publication ID', 'Linked Conference ID'];
      case 'kol-engagements':
        return ['Region', 'Role in Engagement', 'Therapeutic Area', 'Feedback Score', 'Follow Up Action', 'Associated Study ID', 'Associated Publication ID', 'Budget USD', 'Notes'];
      case 'activities':
        return ['Description', 'Therapeutic Area', 'Region', 'Start Date', 'Budget USD', 'Outcome', 'Notes', 'Linked Trial ID', 'Linked Publication ID', 'Linked Conference ID'];
      case 'integrated-evidence':
        return ['Data Source', 'Start Date', 'Key Findings', 'Impact Assessment', 'Next Steps', 'Linked Trial ID'];
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <FiLoader className="h-8 w-8 text-medical-500 animate-spin" />
          <p className="text-secondary-500">Loading details...</p>
        </div>
      </div>
    );
  }

  if (!detailData) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiFileText className="h-8 w-8 text-gray-400" />
        </div>
        <div className="text-secondary-400 text-lg font-medium">No detail data available</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center space-x-4 mb-8">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-xl hover:from-secondary-600 hover:to-secondary-700 transition-all duration-200 shadow-soft"
        >
          <FiArrowLeft className="h-4 w-4" />
          <span>Back to List</span>
        </button>
        <div>
          <h2 className="text-3xl font-bold text-secondary-800">
            {type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ').replace('trial studies', 'Clinical Trials')} Details
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-medical-500 to-medical-600 rounded-full mt-2"></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-8">
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-secondary-700 mb-6 flex items-center space-x-2">
            <div className="w-2 h-2 bg-medical-500 rounded-full"></div>
            <span>Main Information</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {getMainColumns().map((column) => (
              <div key={column} className="space-y-2">
                <label className="text-sm font-medium text-secondary-500">{column}</label>
                <div className="text-sm text-secondary-800 bg-gray-50 px-4 py-3 rounded-xl">
                  {detailData[column.toLowerCase().replace(/ /g, '_')] || '-'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-secondary-700 mb-6 flex items-center space-x-2">
            <div className="w-2 h-2 bg-medical-500 rounded-full"></div>
            <span>Detailed Information</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {getDetailColumns().map((column) => (
              <div key={column} className="space-y-2">
                <label className="text-sm font-medium text-secondary-500">{column}</label>
                <div className="text-sm text-secondary-800 bg-gray-50 px-4 py-3 rounded-xl">
                  {detailData[column.toLowerCase().replace(/ /g, '_')] || '-'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// List page component
const ListPage = ({ type, onRowClick }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const itemsPerPage = 10;

  // Fetch filter options when component mounts or type changes
  const fetchFilterOptions = useCallback(async () => {
    setFilterOptionsLoading(true);
    try {
      const response = await axios.get(getApiUrl(`${type}/filter-options`));
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      setFilterOptions({});
    } finally {
      setFilterOptionsLoading(false);
    }
  }, [type]);

  const fetchData = useCallback(async (page = 1, search = '', filterParams = {}, isSearch = false) => {
    if (isSearch) {
      setSearchLoading(true);
    } else {
      setLoading(true);
    }
    
    try {
      const params = new URLSearchParams({
        skip: ((page - 1) * itemsPerPage).toString(),
        limit: itemsPerPage.toString(),
      });

      if (search) params.append('search', search);
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axios.get(getApiUrl(`${type}?${params}`));
      
      // Handle new paginated response format
      if (response.data.data && response.data.pages) {
        setData(response.data.data);
        setTotalPages(response.data.pages);
      } else {
        // Fallback for old format
        setData(response.data);
        setTotalPages(Math.max(1, Math.ceil(response.data.length / itemsPerPage)));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (isSearch) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [type, itemsPerPage]);

  // Reset page to 1 when type changes
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm('');
    setFilters({});
    fetchFilterOptions();
  }, [type, fetchFilterOptions]);

  useEffect(() => {
    fetchData(currentPage, searchTerm, filters);
  }, [currentPage, searchTerm, filters, type, fetchData]);

  const handleRowClick = (item) => {
    onRowClick(item);
  };

  const handleSearch = (search) => {
    setSearchTerm(search);
    setCurrentPage(1);
    fetchData(1, search, filters, true);
  };

  const handleFilter = (filterParams) => {
    setFilters(filterParams);
    setCurrentPage(1);
    fetchData(1, searchTerm, filterParams, true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getColumns = () => {
    switch (type) {
      case 'publications':
        return ['Publication ID', 'Title', 'Journal', 'Drug', 'Authors', 'Publication Date', 'Status'];
      case 'conferences':
        return ['Conference ID', 'Name', 'Abstract Title', 'Drug', 'Conference Date', 'Location', 'Presentation Type'];
      case 'trial-studies':
        return ['Trial ID', 'NCT Number', 'Title', 'Drug', 'Indication', 'Phase', 'Status', 'Trial Sponsor', 'Estimated Completion Date'];
      case 'kol-engagements':
        return ['KOL ID', 'KOL Name', 'Affiliation', 'Engagement Type', 'Date', 'Topic', 'Strategic Importance'];
      case 'activities':
        return ['Activity ID', 'Title', 'Type', 'Drug', 'Owner', 'Status', 'End Date'];
      case 'integrated-evidence':
        return ['Evidence ID', 'Title', 'Evidence Type', 'Therapeutic Area', 'Lead', 'Completion Date'];
      default:
        return [];
    }
  };

  return (
    <div className="space-y-4 lg:space-y-6 h-full flex flex-col">
      <SearchAndFilter 
        onSearch={handleSearch} 
        onFilter={handleFilter} 
        type={type}
        currentSearchTerm={searchTerm}
        currentFilters={filters}
        filterOptions={filterOptions}
      />
      <div className="flex-1 min-h-0">
        <DataTable 
          data={data} 
          columns={getColumns()} 
          onRowClick={handleRowClick} 
          type={type}
          loading={loading || filterOptionsLoading}
          searchLoading={searchLoading}
        />
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
    </div>
  );
};

// Main App component
const App = () => {
  const [activeTab, setActiveTab] = useState('publications');
  const [currentView, setCurrentView] = useState('list'); // 'list' or 'detail'
  const [selectedItem, setSelectedItem] = useState(null);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentView('list');
    setSelectedItem(null);
  };

  const handleRowClick = (item) => {
    setSelectedItem(item);
    setCurrentView('detail');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedItem(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="flex">
        <Sidebar activeTab={activeTab} onTabChange={handleTabChange} />
        
        <main className={`flex-1 ml-64 lg:ml-80 p-4 lg:p-6 h-screen ${currentView === 'list' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <div className={`${currentView === 'list' ? 'h-full' : 'min-h-full'} max-w-7xl mx-auto`}>
            {currentView === 'list' ? (
              <ListPage type={activeTab} onRowClick={handleRowClick} />
            ) : (
              <DetailPage item={selectedItem} type={activeTab} onBack={handleBackToList} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;